import { ImagePlusIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

import { getImageInitials, getSizeClasses, type ImageSize } from './square-image-upload.helper'

export interface SquareImageUploadButtonProps {
  imageUrl: string | null
  fallbackText: string
  isDisabled: boolean
  size: ImageSize
  uploadTooltip: string
  changeTooltip: string
  onClick: () => void
}

export const SquareImageUploadButton = ({
  imageUrl,
  fallbackText,
  isDisabled,
  size,
  uploadTooltip,
  changeTooltip,
  onClick,
}: SquareImageUploadButtonProps): ReactNode => {
  const { sizeClass, textSizeClass, iconSizeClass } = getSizeClasses(size)

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          disabled={isDisabled}
          className={cn(
            'square-image-upload-button relative block rounded-xl focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            !isDisabled && 'cursor-pointer',
            isDisabled && 'cursor-not-allowed opacity-50'
          )}
        >
          <Avatar className={cn('square-image-upload-avatar rounded-xl', sizeClass)}>
            {imageUrl && <AvatarImage src={imageUrl} alt={fallbackText || 'Image'} />}
            <AvatarFallback className={cn('rounded-xl bg-primary/10 text-primary', textSizeClass)}>
              {getImageInitials(fallbackText)}
            </AvatarFallback>
          </Avatar>
          {!isDisabled && (
            <div
              className={cn(
                'absolute inset-0 flex items-center justify-center rounded-xl bg-black/50 opacity-0 transition-opacity',
                'group-hover:opacity-100'
              )}
            >
              <ImagePlusIcon className={cn('text-white', iconSizeClass)} />
            </div>
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{imageUrl ? changeTooltip : uploadTooltip}</TooltipContent>
    </Tooltip>
  )
}

SquareImageUploadButton.displayName = 'SquareImageUploadButton'
